import { useState, useEffect, useRef } from "react";
import trashImg from "assets/images/admin/trash_black.svg";
import style from "./MarathonPartnerLogo.module.scss";
import {
  FieldArrayWithId,
  Path,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormRegister,
  useFormContext,
} from "react-hook-form";
import { ICreateMarathon } from "features/createEditMarathon/model/types";
import { UiTextField } from "shared/ui/UiInputs/UiTextField";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ErrorMessage } from "shared/ui/ErrorMessage/ErrorMessage";

interface MarathonPartnerLogoProps {
  appendPartnerCompany: UseFieldArrayAppend<
    ICreateMarathon,
    `partners.${number}.partnerCompanies`
  >;
  removePartnerCompany: UseFieldArrayRemove;
  index: number;
  register: UseFormRegister<ICreateMarathon>;
  index2: number;
  item: FieldArrayWithId<
    ICreateMarathon,
    `partners.${number}.partnerCompanies`,
    "id"
  >;
}

export const MarathonPartnerLogo = ({
  appendPartnerCompany,
  index,
  removePartnerCompany,
  register,
  index2,
  item: data,
}: MarathonPartnerLogoProps) => {
  const [imgUrl, setImgUrl] = useState<string>("");
  const blobRef = useRef<string | null>(null);
  const hydratedRef = useRef<string | null>(null);
  const { t } = useTranslation();

  const {
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext<ICreateMarathon>();

  const {
    onBlur: rhfUrlOnBlur,
    ...urlRegister
  } = register(
    `partners.${index}.partnerCompanies.${index2}.url`,
    {
      required: t("errors.required"),
      pattern: {
        value: /^https:\/\/.+/i,
        message: t("errors.onlyHttps"),
      },
    }
  );

  const getFileBase = (): string => {
    const serverUrl = process.env.REACT_APP_SERVER_URL || "";
    const defaultUrl = process.env.REACT_APP_DEFAULT_IMAGE_URL || "https://marathon-app.timelysoft.org";
    const base = serverUrl.replace(/\/api(\/v1)?\/?$/i, "");
    return base || defaultUrl;
  };


  const joinUrl = (base: string, path: string) => {
    const b = (base || "").replace(/\/+$/, "");
    const p = (path || "").replace(/^\/+/, "");
    const encoded = p.split("/").map((s) => encodeURIComponent(s)).join("/");
    return `${b}/${encoded}`;
  }

  const toAbsolute = (url: string) => {
    if (/^(https?:|blob:|data:)/i.test(url)) return encodeURI(url);
    const base = getFileBase();
    if (url.startsWith("/staticfiles/") || url.startsWith("/app/")) return joinUrl(base, url);
    if (url.startsWith("staticfiles/") || url.startsWith("app/")) return joinUrl(base, `/${url}`);
    return joinUrl(base, `/${url.replace(/^\/+/, "")}`);
  }

  const filenameFromUrl = (absUrl: string, contentType?: string): string => {
    try {
      const u = new URL(absUrl);
      const raw = decodeURIComponent(u.pathname.split("/").pop() || "");
      if (raw) return raw;
    } catch { }
    const ext =
      contentType?.includes("png") ? "png" :
        contentType?.includes("webp") ? "webp" :
          contentType?.includes("gif") ? "gif" :
            contentType?.includes("jpeg") ? "jpg" : "bin";
    return `image.${ext}`;
  }

  const fetchAsFile = async (abs: string): Promise<File | null> => {
    let r: Response | null = null;

    try {
      r = await fetch(abs, { cache: "no-store" as RequestCache });
    } catch { }

    if (!r || !r.ok || !/^image\//i.test(r.headers.get("content-type") || "")) {
      try {
        r = await fetch(abs, {
          credentials: "include" as RequestCredentials,
          cache: "no-store" as RequestCache,
        });
      } catch { }
    }

    if (!r || !r.ok) return null;

    const ct = r.headers.get("content-type") || "";
    if (!/^image\//i.test(ct)) return null;

    const blob = await r.blob();
    const name = filenameFromUrl(abs, blob.type || ct);
    return new File([blob], name, { type: blob.type || ct });
  }

  const withCacheBuster = (absUrl: string): string => {
    return `${absUrl}${absUrl.includes("?") ? "&" : "?"}t=${Date.now()}`;
  }

  function trySetLogoFile(file: File): Path<ICreateMarathon> | null {
    const candidates = [ /* ... */] as const;
    for (const p of candidates) {
      const path = p as unknown as Path<ICreateMarathon>;
      const current = getValues(path);
      if (current !== undefined) {
        setValue(path, file, { shouldDirty: true, shouldValidate: true });
        return path;
      }
    }
    return null;
  }


  const deleteLogo = () => {
    removePartnerCompany(index2);
  };


  useEffect(() => {
    if (blobRef.current) {
      try { URL.revokeObjectURL(blobRef.current); } catch { }
      blobRef.current = null;
    }

    if (!data?.logo) {
      hydratedRef.current = null;
      setImgUrl("");
      return;
    }

    if (data.logo instanceof File) {
      const u = URL.createObjectURL(data.logo);
      blobRef.current = u;
      setImgUrl(u);
      return;
    }

    if (
      data.logo &&
      typeof data.logo === "object" &&
      "length" in (data.logo as any) &&
      (data.logo as any).length > 0
    ) {
      const file = (data.logo as any)[0] as File;
      if (file instanceof File) {
        const u = URL.createObjectURL(file);
        blobRef.current = u;
        setImgUrl(u);
        return;
      }
    }

    const raw =
      typeof data.logo === "string"
        ? data.logo
        : (data.logo as any)?.url || (data.logo as any)?.path;

    if (!raw || typeof raw !== "string") {
      setImgUrl("");
      return;
    }

    const abs = toAbsolute(raw);
    const show = withCacheBuster(abs);
    setImgUrl(show);

    if (hydratedRef.current === abs) return;

    let cancelled = false;
    (async () => {
      const asFile = await fetchAsFile(abs);
      if (!asFile || cancelled) return;
      hydratedRef.current = abs;

      const path = trySetLogoFile(asFile);
      if (!path) {
        const u = URL.createObjectURL(asFile);
        blobRef.current = u;
        setImgUrl(`${u}#${Date.now()}`);
      }
    })();

    return () => { cancelled = true; };
  }, [data.logo, index, index2]);

  useEffect(() => {
    return () => {
      if (blobRef.current) {
        try { URL.revokeObjectURL(blobRef.current); } catch { }
        blobRef.current = null;
      }
    };
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        width: "100%",
        background: "#FAFAFA",
        minHeight: "120px",
      }}
    >
      <Box sx={{ width: "30%", height: "100%" }}>
        <div className={style.box} style={{ height: "110px" }}>
          {imgUrl ? (
            <img
              height={"110px"}
              width={"100%"}
              style={{ objectFit: "cover" }}
              src={imgUrl}
              alt="Логотип партнёра"
              onError={(e) => {
                e.currentTarget.style.border = "2px solid #d4322a";
                e.currentTarget.title = "Ошибка загрузки изображения";
              }}
            />
          ) : (
            <div
              style={{
                height: "110px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px dashed #ccc",
                width: "100%",
                background: "#f5f5f5",
              }}
            >
              <span>Нет изображения</span>
            </div>
          )}
          <div className={style.box_trash} onClick={deleteLogo}>
            <img src={trashImg} alt="Удалить партнёра" />
          </div>
        </div>
      </Box>
      <Box sx={{ width: "70%" }}>
        <UiTextField
          error={
            errors?.partners?.[index]?.partnerCompanies?.[index2]?.name?.message
          }
          label={t("createMarathon.name")}
          inputProps={{
            // @ts-ignore
            ...register(`partners.${index}.partnerCompanies.${index2}.name`),
          }}
        />
        <UiTextField
          label={t("createMarathon.link")}
          error={!!errors?.partners?.[index]?.partnerCompanies?.[index2]?.url}
          helperText={
            errors?.partners?.[index]?.partnerCompanies?.[index2]?.url?.message
          }
          onBlur={(e) => {
            rhfUrlOnBlur(e);

            const value = e.target.value.trim();
            if (value && !value.startsWith("https://")) {
              setValue(
                `partners.${index}.partnerCompanies.${index2}.url`,
                `https://${value}`,
                { shouldDirty: true, shouldValidate: true }
              );
            }
          }}
          inputProps={{
            type: "url",
            ...urlRegister,
          }}
        />
        <ErrorMessage
          error={
            errors?.partners?.[index]?.partnerCompanies?.[index2]?.logo?.message
          }
        />
      </Box>
    </Box>
  );
}
